import { Node, ReactRenderer } from '@tiptap/react'
import tippy, { type Instance, type Props } from 'tippy.js'
import { RefAttributes, forwardRef } from 'react'
import {
  Suggestion,
  SuggestionKeyDownProps,
  SuggestionProps,
} from '@tiptap/suggestion'
import { PluginKey } from '@tiptap/pm/state'
import {
  MentionControllerProps,
  MentionsListController,
} from '../mentions-list'
import { MentionOptions } from '../_types'
import { Block } from '@basehub/data/replicache/index'
import { clsx } from 'clsx'
import { BlockIcon } from '~/components/block-icon'
import { BlockMentionNodeView } from './node-view'
import { tabContentWrapperId } from '../../../../../../tab-content/wrapper'
import { getBlockTitleForDisplay } from '~/lib/get-block-title-for-display'
import { TreeNode } from '@basehub/utils/tree/index'

export const BlockMentionPluginKey = new PluginKey('BlockMentionPluginKey')

export type BlockMentionProps = {
  block: Block
  paths: Block[]
}

export const BlockMention = Node.create<
  MentionOptions<BlockMentionProps> & {
    getNode: (id: string) => TreeNode<Block> | null
  }
>({
  name: 'block-mention',
  HTMLAttributes: {},
  deleteTriggerWithBackspace: false,
  group: 'inline',
  inline: true,
  selectable: false,
  atom: true,
  priority: 100,
  // @ts-ignore
  addOptions() {
    return {
      suggestion: {
        char: '#',
      },
      getNode: (_id: string) => {
        return undefined
      },
    }
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-id'),
        renderHTML: (attributes) => {
          if (!attributes.id) {
            return {}
          }

          return {
            'data-id': attributes.id,
          }
        },
      },
      label: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-label'),
        renderHTML: (attributes) => {
          if (!attributes.label) {
            return {}
          }

          return {
            'data-label': attributes.label,
          }
        },
      },
      'block-type': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-block-type'),
        renderHTML: (attributes) => {
          if (!attributes.type) {
            return {}
          }

          return {
            'data-block-type': attributes.type,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: `span[data-type="${this.name}"]`,
      },
    ]
  },

  addNodeView() {
    return ({ editor, node, getPos, decorations, extension }) => {
      const renderer = new ReactRenderer(BlockMentionNodeView, {
        editor,
        props: {
          items: this.options.suggestion!.items!({
            query: null,
            editor,
          }),
          node,
          getPos,
          editor,
          decorations,
          extension,
          getNode: this.options.getNode,
        },
        as: 'span',
      })
      return {
        dom: renderer.element,
      }
    }
  },

  renderText({ node }) {
    const items = this.options.suggestion!.items!({
      query: '',
      editor: this.editor!,
    })

    const item = items.find((item) => item.block.id === node.attrs.id)
    if (!item) {
      return `${this.options.suggestion!.char}${
        node.attrs.label ?? node.attrs.id
      }`
    }
    const name = `${getBlockTitleForDisplay(item.block.title)}`
    return `${this.options.suggestion!.char}${name}`
  },

  renderHTML({ node }) {
    const items = this.options.suggestion?.items?.({
      query: null,
      editor: this.editor!,
    })
    const item = items?.find((item) => item.block.id === node.attrs.id)
    if (!item) {
      return [
        'span',
        {
          'data-type': this.name,
          'data-id': node.attrs.id,
          'data-label': node.attrs.label,
        },
        `${this.options.suggestion!.char}${node.attrs.label ?? node.attrs.id}`,
      ]
    }
    const name = `${getBlockTitleForDisplay(item.block.title)}`
    return [
      'span',
      {
        'data-type': this.name,
        'data-id': node.attrs.id,
        'data-label': node.attrs.label,
      },
      `${this.options.suggestion!.char}${name}`,
    ]
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          let isMention = false
          const { selection } = state
          const { empty, anchor } = selection

          if (!empty) {
            return false
          }

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              isMention = true
              tr.insertText(
                this.options.deleteTriggerWithBackspace
                  ? ''
                  : this.options.suggestion.char || '',
                pos,
                pos + node.nodeSize
              )

              return false
            }
          })

          return isMention
        }),
    }
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        char: '#',
        pluginKey: BlockMentionPluginKey,
        command: ({ editor, range, props }) => {
          // increase range.to by one when the next node is of type "text"
          // and starts with a space character
          const nodeAfter = editor.view.state.selection.$to.nodeAfter
          const overrideSpace = nodeAfter?.text?.startsWith(' ')

          if (overrideSpace) {
            range.to += 1
          }

          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: this.name,
                attrs: {
                  id: props.block.id,
                  label: `${getBlockTitleForDisplay(props.block.title)}`,
                },
              },
              {
                type: 'text',
                text: ' ',
              },
            ])
            .run()

          window.getSelection()?.collapseToEnd()
        },
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from)
          const type = state.schema.nodes[this.name]
          const allow = !!$from.parent.type.contentMatch.matchType(type as any)

          return allow
        },
        render: () => {
          let isOpenInternal = false
          let component:
            | ReactRenderer<
                ReactRenderer['component'],
                MentionControllerProps<BlockMentionProps> &
                  RefAttributes<ReactRenderer['component']>
              >
            | undefined
          let popup: Instance<Props>

          const onExitBase = (onCloseCallback?: () => void) => {
            if (!isOpenInternal) return
            isOpenInternal = false
            popup?.destroy?.()
            component?.destroy?.()
            onCloseCallback?.()
            this.editor.view.dom.classList.remove('slash-command-open')
          }

          return {
            onStart: (props: SuggestionProps<BlockMentionProps>) => {
              if (!props.editor.isFocused) {
                // prevent open
                // sometimes this fires without the editor being focused!
                isOpenInternal = false
                return
              }
              isOpenInternal = true
              // this.options.onOpen()
              this.editor.view.dom.classList.add('slash-command-open')
              component = new ReactRenderer(BlockMentionsList, {
                editor: props.editor,
                props: {
                  ...props,
                  onExit: () => {
                    onExitBase?.()
                  },
                },
              })

              const tabContent = document.querySelector<HTMLDivElement>(
                `#${tabContentWrapperId}`
              )
              if (!tabContent) {
                throw new Error('tabContent is null or undefined')
              }

              const i = tippy(tabContent, {
                getReferenceClientRect: () => {
                  const clientRect = props.clientRect?.()
                  if (!clientRect) {
                    throw new Error('clientRect is null or undefined')
                  }
                  return clientRect
                },
                appendTo: () => document.body,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: 'manual',
                placement: 'bottom-start',
                zIndex: 71,
              })

              popup = i
            },

            onUpdate(props: SuggestionProps<BlockMentionProps>) {
              component?.updateProps({
                ...props,
              })

              popup?.setProps({
                getReferenceClientRect: () => {
                  const clientRect = props.clientRect?.()
                  if (!clientRect) {
                    throw new Error('clientRect is null or undefined')
                  }
                  return clientRect
                },
              })
            },

            onKeyDown(props: SuggestionKeyDownProps) {
              if (props.event.key === 'Escape') {
                popup?.hide()

                return true
              }
              if (!component) {
                return false
              }
              return component.ref?.onKeyDown(props)
            },

            onExit() {
              popup?.destroy?.()
              component?.destroy?.()
              onExitBase?.()
            },
          }
        },

        ...this.options.suggestion,
      }),
    ]
  },
})

const renderBlock: MentionControllerProps<BlockMentionProps>['renderItem'] = ({
  isSelected,
  item,
  className,
  ...rest
}) => {
  const { block, paths } = item
  return (
    <button
      className={clsx(
        className,
        'flex gap-2 items-center cursor-default px-3 !h-10 text-dark-muted max-w-full',
        isSelected && 'bg-dark-shade-hover'
      )}
      data-selected={isSelected}
      {...rest}
    >
      <BlockIcon
        iconProps={{
          className: 'shrink-0',
        }}
        block={block}
      />
      <span className="flex gap-0.5 max-w-full text-xs leading-none flex-1 pr-4 h-10 items-center">
        <span className="text-dark-dim whitespace-nowrap truncate shrink">
          {getBlockTitleForDisplay(block.title)}
        </span>
        {paths && paths.length > 0 && (
          <span className="text-dark-muted text-xs whitespace-nowrap text-left truncate basis-[12ch] shrink-0 ml-2">
            {paths
              .map((path) => getBlockTitleForDisplay(path.title))
              .join(' / ')}
          </span>
        )}
      </span>
    </button>
  )
}

export const BlockMentionsList = forwardRef(
  (
    props: MentionControllerProps<BlockMentionProps>,
    ref: React.Ref<ReactRenderer['component']>
  ) => (
    <MentionsListController<BlockMentionProps>
      {...props}
      ref={ref}
      renderItem={renderBlock}
    />
  )
)
